import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanPlasticSign extends FloorplanProductItem<PlasticSignConfiguration> {
  @IsString()
  @Expose({ name: "plasticSignId" })
  private readonly _plasticSignId: string;

  constructor(
    floorplan: Floorplan,
    plasticSign: PlasticSignConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, plasticSign, id, x, y, size, notes);
    this._plasticSignId = plasticSign?.id;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.plasticSignsItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanPlasticSign) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanPlasticSign.create(this._floorplan, config, x, y, size);
  }

  override delete() {
    this._floorplan.deletePlasticSign(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.plasticSigns;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanPlasticSign,
        this._floorplan.plasticSigns,
        FloorplanEventType.PLASTIC_SIGN_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanPlasticSign) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshPlasticSignsLock();
  }

  static create(
    floorplan: Floorplan,
    plasticSign: PlasticSignConfiguration,
    x: number,
    y: number,
    size: number,
  ): FloorplanPlasticSign {
    return new FloorplanPlasticSign(floorplan, plasticSign, uuidv4(), x, y, size);
  }

  protected override getConfigFromProject(floorplan: Floorplan): PlasticSignConfiguration | undefined {
    return floorplan.getPlasticSignConfig(this._plasticSignId);
  }
}
